import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp';
import { MessageCircle, Phone, Video, ArrowRight, RefreshCw, Trash2, MoreVertical, X } from 'lucide-react';
import { motion } from 'framer-motion';
import BottomNavigation from './BottomNavigation';
import { getUserChats, syncChatsWithDatabase } from '../services/chatService';
import toast from 'react-hot-toast';

const Chats = () => {
  const navigate = useNavigate();
  const { language, chats, user } = useApp();

  // Debug logging
  console.log('🔍 Chats component - chats from context:', chats?.length || 0, 'chats');
  console.log('🔍 Chats component - current user:', user?.uid);

  // State for chats loaded from database
  const [databaseChats, setDatabaseChats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all'); // all, active, completed
  const [showMenu, setShowMenu] = useState(null); // For dropdown menu
  
    // Function to enhance chat data with lawyer information from database
  const enhanceChatWithLawyerData = async (chat) => {
    try {
      const lawyerId = chat.lawyer?.id || chat.lawyerId;
      if (!lawyerId) {
        return chat;
      }
      
      // Try to get lawyer data from database
      const { db } = await import('../config/firebase');
      const { doc, getDoc } = await import('firebase/firestore');
      
      const lawyerDoc = await getDoc(doc(db, 'lawyers', lawyerId));
      if (lawyerDoc.exists()) {
        const lawyerData = lawyerDoc.data();
        console.log('🔍 Found lawyer data for ID:', lawyerId, 'Profile pic:', lawyerData.profile_pic, 'Specialization:', lawyerData.specialization);
        return {
          ...chat,
          lawyerProfilePic: lawyerData.profile_pic || chat.lawyerProfilePic || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
          specialization: (() => {
            const spec = lawyerData.specialization || chat.specialization || 'General Law';
            if (Array.isArray(spec)) {
              return spec.join(', ');
            }
            return spec;
          })(),
          lawyer: {
            ...chat.lawyer,
            specialization: (() => {
              const spec = lawyerData.specialization || chat.lawyer?.specialization || 'General Law';
              if (Array.isArray(spec)) {
                return spec.join(', ');
              }
              return spec;
            })()
          }
        };
      } else {
        console.log('🔍 No lawyer found in database for ID:', lawyerId);
      }
    } catch (error) {
      console.log('🔍 Error enhancing chat with lawyer data:', error);
    }
    
    return chat;
  };
  
  // Function to load chats from database
  const loadChatsFromDatabase = async () => {
    if (!user?.uid) {
      console.log('🔍 No user ID, cannot load chats from database');
      setIsLoading(false);
      return;
    }

    try {
             console.log('🔍 Loading chats from database for user:', user.uid);
      setIsLoading(true);
      
             // Get chats from database
       const result = await getUserChats(user.uid);
      
      if (result.success && result.data) {
        console.log('🔍 Successfully loaded', result.data.length, 'chats from database');
        
        // Enhance chats with lawyer data
        const enhancedChats = await Promise.all(result.data.map(enhanceChatWithLawyerData));
        
        // Update localStorage as backup
        localStorage.setItem('userChats', JSON.stringify(enhancedChats));
        
        setDatabaseChats(enhancedChats);
      } else {
        console.log('❌ Failed to load chats from database:', result.error);
        // Fallback to localStorage
        const localChats = JSON.parse(localStorage.getItem('userChats') || '[]');
        setDatabaseChats(localChats);
      }
    } catch (error) {
      console.error('🔍 Error loading chats from database:', error);
      // Fallback to localStorage
      const localChats = JSON.parse(localStorage.getItem('userChats') || '[]');
      setDatabaseChats(localChats);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to sync with database
  const handleSyncWithDatabase = async () => {
    if (!user?.uid) {
      console.log('🔍 No user ID, cannot sync with database');
      return;
    }

    try {
      console.log('🔄 Syncing with database...');
             const result = await syncChatsWithDatabase(user.uid);
      
      if (result.success) {
        setDatabaseChats(result.data);
        console.log('✅ Synced successfully');
      } else {
        console.log('❌ Sync failed');
      }
    } catch (error) {
      console.error('🔍 Error syncing with database:', error);
    }
  };

  useEffect(() => {
    // Only load chats if user is available
    if (user?.uid) {
      console.log('🔍 User available, loading chats from database');
      loadChatsFromDatabase();
      
      // Auto-cleanup duplicate chats on app load
      const autoCleanup = async () => {
        try {
          console.log('🧹 Auto-cleanup of duplicate chats...');
          const { chatService } = await import('../services/firebaseService');
          const result = await chatService.cleanupDuplicateChats();
          
          if (result.success && result.duplicatesFound > 0) {
            console.log(`✅ Auto-cleanup completed: ${result.duplicatesDeleted} duplicates removed`);
            // Reload chats after cleanup
            loadChatsFromDatabase();
          }
        } catch (error) {
          console.error('❌ Auto-cleanup failed:', error);
        }
      };
      
      // Run cleanup after a short delay to ensure user is fully loaded
      setTimeout(autoCleanup, 2000);
    } else {
      console.log('🔍 No user available, skipping chat loading');
      setIsLoading(false);
    }

    // Listen for chat updates
    const handleChatUpdate = () => {
      if (user?.uid) {
        loadChatsFromDatabase();
      }
    };

    window.addEventListener('chatUpdated', handleChatUpdate);
    
    return () => {
      window.removeEventListener('chatUpdated', handleChatUpdate);
    };
  }, [user?.uid]);

  // Use database chats if available, otherwise fallback to context or local chats
  const allChats = databaseChats.length > 0 ? databaseChats : (chats && chats.length > 0 ? chats : []);
  
  // Filter chats based on status
  const displayChats = allChats.filter(chat => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'active') {
      return chat.status === 'active' || chat.status === 'pending' || chat.status === 'confirmed' || chat.status === 'ongoing';
    }
    if (activeFilter === 'completed') {
      return chat.status === 'completed' || chat.status === 'finished';
    }
    return true;
  });

  // Get counts for each filter
  const getFilterCounts = () => {
    const allCount = allChats.length;
    const activeCount = allChats.filter(chat => 
      chat.status === 'active' || chat.status === 'pending' || chat.status === 'confirmed' || chat.status === 'ongoing'
    ).length;
    const completedCount = allChats.filter(chat => 
      chat.status === 'completed' || chat.status === 'finished'
    ).length;
    
    return { allCount, activeCount, completedCount };
  };

  const filterCounts = getFilterCounts();
  
  console.log('🔍 Chats component - displayChats:', displayChats?.length || 0, 'chats');
  console.log('🔍 Chats component - isLoading:', isLoading);

  const translations = {
    english: {
      chats: 'Chats',
      legalConsultations: 'Your legal consultations',
      online: 'Online',
      message: 'Message',
      call: 'Call',
      video: 'Video',
      noChats: 'No chats found',
      noChatsDesc: 'You haven\'t started any conversations yet. Book a consultation to start chatting!',
      all: 'All',
      active: 'Active',
      completed: 'Completed',
      deleteChat: 'Delete Chat',
      confirmDelete: 'Are you sure you want to delete this chat?'
    },
    hindi: {
      chats: 'चैट',
      legalConsultations: 'आपके कानूनी परामर्श',
      online: 'ऑनलाइन',
      message: 'संदेश',
      call: 'कॉल',
      video: 'वीडियो',
      noChats: 'कोई चैट नहीं मिली',
      noChatsDesc: 'आपने अभी तक कोई बातचीत शुरू नहीं की है। चैटिंग शुरू करने के लिए परामर्श बुक करें!',
      all: 'सभी',
      active: 'सक्रिय',
      completed: 'पूर्ण',
      deleteChat: 'चैट हटाएं',
      confirmDelete: 'क्या आप वाकई इस चैट को हटाना चाहते हैं?'
    }
  };

  const t = translations[language] || translations.english;

  const handleChatClick = (chatId) => {
    // Find the chat data to pass to the conversation
    const chat = displayChats.find(c => c.id === chatId);
    console.log('🔍 Chat clicked, navigating to conversation:', chatId, 'with chat data:', chat);
    navigate(`/chat-conversation/${chatId}`, { state: { chat } });
  };

  const handleAction = (e, action, chatId) => {
    e.stopPropagation();
    if (action === 'message') {
      // Find the chat data to pass to the conversation
      const chat = displayChats.find(c => c.id === chatId);
      console.log('🔍 Navigating to chat conversation:', chatId, 'with chat data:', chat);
      navigate(`/chat-conversation/${chatId}`, { state: { chat } });
    } else {
      // Handle call/video actions
      console.log(`${action} action for chat ${chatId}`);
    }
  };

  const handleRefreshChats = () => {
    console.log('🔄 Manually refreshing chats...');
    loadChatsFromDatabase().catch(console.error);
  };

  const handleMenuClick = (e, chatId) => {
    e.stopPropagation();
    setShowMenu(showMenu === chatId ? null : chatId);
  };

  const handleDeleteChat = async (chatId) => {
    // Show confirmation dialog
    if (!window.confirm(t.confirmDelete)) {
      return;
    }
    
    try {
      // Remove from database if user is logged in
      if (user?.uid) {
        console.log('🗑️ Deleting chat from database:', chatId);
        
                 // Import Firebase functions
         const { db } = await import('../config/firebase');
         const { doc, deleteDoc, collection, getDocs } = await import('firebase/firestore');
        
        try {
          // Delete the chat document
          const chatRef = doc(db, 'chats', chatId);
          await deleteDoc(chatRef);
          console.log('✅ Chat document deleted from database');
          
          // Delete all messages in the chat subcollection
          const messagesRef = collection(db, 'chats', chatId, 'messages');
          const messagesSnapshot = await getDocs(messagesRef);
          
                     const deletePromises = messagesSnapshot.docs.map(doc => deleteDoc(doc.ref));
          await Promise.all(deletePromises);
          console.log('✅ Chat messages deleted from database');
          
        } catch (dbError) {
          console.error('❌ Error deleting from database:', dbError);
          // Continue with localStorage cleanup even if database deletion fails
        }
      }
      
      // Remove from localStorage
      const allChats = JSON.parse(localStorage.getItem('userChats') || '[]');
      const updatedChats = allChats.filter(chat => chat.id !== chatId);
      localStorage.setItem('userChats', JSON.stringify(updatedChats));
      
      // Remove chat messages from localStorage
      localStorage.removeItem(`chat_messages_${chatId}`);
      
      // Update the local state to reflect the change
      setDatabaseChats(prevChats => prevChats.filter(chat => chat.id !== chatId));
      
      // Close menu
      setShowMenu(null);
      
      console.log('✅ Chat deleted successfully:', chatId);
      
      // Show success notification
      toast.success('Chat deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting chat:', error);
      toast.error('Failed to delete chat');
    }
  };

  // Function to format date with better timing display
  const formatDate = (dateString) => {
    if (!dateString) return 'Just now';
    
    try {
      // Handle different date formats
      let date;
      if (typeof dateString === 'string') {
        // If it's already a formatted string like "Just now", return it
        if (dateString === 'Just now' || dateString.includes('ago')) {
          return dateString;
        }
        date = new Date(dateString);
      } else if (dateString?.toDate) {
        // Handle Firestore Timestamp
        date = dateString.toDate();
      } else {
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) {
        console.log('🔍 Invalid date:', dateString);
        return 'Just now';
      }
      
      const now = new Date();
      const diffInMs = now - date;
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
      
      // If less than 1 minute, show "Just now"
      if (diffInMinutes < 1) {
        return 'Just now';
      }
      
      // If less than 1 hour, show minutes
      if (diffInMinutes < 60) {
        return `${diffInMinutes}m ago`;
      }
      
      // If less than 24 hours, show hours
      if (diffInHours < 24) {
        return `${diffInHours}h ago`;
      }
      
      // If less than 7 days, show day name
      if (diffInDays < 7) {
        return date.toLocaleDateString('en-US', { weekday: 'short' });
      }
      
      // If less than 30 days, show date
      if (diffInDays < 30) {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
      
      // Otherwise show DD:MM:YY format
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear().toString().slice(-2);
      
      return `${day}:${month}:${year}`;
    } catch (error) {
      console.error('Error formatting date:', error, 'Input:', dateString);
      return 'Just now';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
            aria-label="Go back"
          >
            <ArrowRight className="w-5 h-5 text-gray-600 rotate-180" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {t.chats}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {t.legalConsultations}
            </p>
          </div>
          
          {/* Cleanup Duplicate Chats Button */}
          <button
            onClick={async () => {
              try {
                toast.loading('Cleaning up duplicate chats...');
                const { chatService } = await import('../services/firebaseService');
                const result = await chatService.cleanupDuplicateChats();
                
                if (result.success) {
                  toast.dismiss();
                  if (result.duplicatesFound > 0) {
                    toast.success(`Cleaned up ${result.duplicatesDeleted} duplicate chats!`);
                    // Reload chats after cleanup
                    loadChatsFromDatabase();
                  } else {
                    toast.success('No duplicate chats found!');
                  }
                } else {
                  toast.dismiss();
                  toast.error('Failed to cleanup chats: ' + result.error);
                }
              } catch (error) {
                toast.dismiss();
                toast.error('Error during cleanup: ' + error.message);
              }
            }}
            className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
            title="Clean up duplicate chats"
          >
            🧹 Clean
          </button>
        </div>
        
        {/* Filter Tabs */}
        <div className="flex bg-gray-100 dark:bg-gray-700 rounded-xl p-1">
          {[
            { id: 'all', label: t.all, count: filterCounts.allCount },
            { id: 'active', label: t.active, count: filterCounts.activeCount },
            { id: 'completed', label: t.completed, count: filterCounts.completedCount }
          ].map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeFilter === filter.id
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <span>{filter.label}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeFilter === filter.id
                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                    : 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-400'
                }`}>
                  {filter.count}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="px-6 py-6">
                 {isLoading ? (
           <motion.div
             initial={{ opacity: 0 }}
             animate={{ opacity: 1 }}
             className="text-center py-12"
           >
             <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
             <p className="text-gray-500">Loading chats...</p>
           </motion.div>
         ) : displayChats.length > 0 ? (
          <div className="space-y-4">
            {displayChats.map((chat, index) => (
              <motion.div
                key={`${chat.id}_${chat.lawyer?.id || chat.lawyerId || index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                onClick={() => handleChatClick(chat.id)}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4">
                    <div className="relative">
                      <img
                        src={chat.lawyerProfilePic || chat.profilePic || chat.lawyer?.profile_pic || chat.lawyer?.profilePic || chat.profile_pic || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'}
                        alt={chat.lawyerName || chat.lawyer?.name || chat.lawyer?.full_name || chat.lawyer?.fullName || chat.lawyer_name || 'Lawyer'}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                      {(chat.lawyer?.isOnline || chat.isOnline) && (
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {chat.lawyerName || chat.lawyer?.name || chat.lawyer?.full_name || chat.lawyer?.fullName || chat.lawyer_name || 'Lawyer'}
                        </h3>
                        {(chat.lawyer?.isOnline || chat.isOnline) && (
                          <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                            {t.online}
                          </span>
                        )}
                      </div>
                        <p className="text-purple-600 dark:text-purple-400 font-medium mb-2">
                          {(() => {
                            const spec = chat.specialization || chat.lawyer?.specialization || chat.lawyer?.specializations || chat.specializations || 'General Law';
                            if (Array.isArray(spec)) {
                              return spec.join(', ');
                            }
                            return spec;
                          })()}
                        </p>
                      <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2">
                        {chat.lastMessage || 'No messages yet'}
                      </p>
                    </div>
                  </div>
                                       <div className="text-right relative">
                       <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                         {(() => {
                           const timestamp = chat.lastMessageTime || chat.timestamp;
                           console.log('🔍 Chat timestamp for', chat.lawyerName || 'Lawyer', ':', timestamp);
                           return formatDate(timestamp);
                         })()}
                       </p>
                       <div className="flex items-center gap-2 justify-end">
                         {chat.unreadCount > 0 && (
                           <div className="inline-flex items-center justify-center w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full">
                             {chat.unreadCount}
                           </div>
                         )}
                         <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                           chat.status === 'completed' || chat.status === 'finished' 
                             ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                             : chat.status === 'active' || chat.status === 'pending' || chat.status === 'confirmed' || chat.status === 'ongoing'
                             ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                             : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                         }`}>
                           {chat.status || 'pending'}
                         </span>
                         
                         {/* Menu Button */}
                         <button
                           onClick={(e) => handleMenuClick(e, chat.id)}
                           className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors duration-200"
                         >
                           <MoreVertical className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                         </button>
                         
                         {/* Dropdown Menu */}
                         {showMenu === chat.id && (
                           <div className="absolute right-0 top-8 z-10 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 min-w-[160px]">
                             <button
                               onClick={(e) => handleDeleteChat(chat.id)}
                               className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200"
                             >
                               <Trash2 className="w-4 h-4" />
                               {t.deleteChat}
                             </button>
                           </div>
                         )}
                       </div>
                     </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={(e) => handleAction(e, 'message', chat.id)}
                    className="flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors duration-200"
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">{t.message}</span>
                  </button>
                  <button
                    onClick={(e) => handleAction(e, 'call', chat.id)}
                    className="flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors duration-200"
                  >
                    <Phone className="w-4 h-4" />
                    <span className="text-sm font-medium">{t.call}</span>
                  </button>
                  <button
                    onClick={(e) => handleAction(e, 'video', chat.id)}
                    className="flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors duration-200"
                  >
                    <Video className="w-4 h-4" />
                    <span className="text-sm font-medium">{t.video}</span>
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {t.noChats}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t.noChatsDesc}
            </p>
            <button
              onClick={() => navigate('/find-lawyer')}
              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
            >
              Find a Lawyer
            </button>
          </motion.div>
        )}
      </div>

      {/* Click outside to close menu */}
      {showMenu && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setShowMenu(null)}
        />
      )}

      {/* Bottom Navigation */}
      <BottomNavigation activePage="chat" />
    </div>
  );
};

export default Chats;